import dedent from 'ts-dedent'
import {
  ColumnDataType,
  GetOutputSchemaParams,
  Language,
  Provider,
  QueryAndOutputs,
  TableSchema,
  WorkflowComponentCodeParams,
  WorkflowComponentParamType
} from '../types'
import { DBricksConnection } from '../workflow/connections/databricks'
import { SingleOutputWorkflowComponent } from './common/singleoutputcomponent'

class ClipWithPolygonsDB extends SingleOutputWorkflowComponent {
  constructor() {
    super(
      {
        name: 'native.clipwithpolygons',
        group: 'Spatial Constructors',
        title: 'Clip with Polygons',
        description: `
      This component creates a new table containing the geographies of the input
      table clipped to the combined boundary of the geographies in another table.
      `,
        starred: true
      },
      [
        {
          name: 'source',
          type: WorkflowComponentParamType.InputTable,
          title: 'Table to clip'
        },
        {
          name: 'polygons',
          type: WorkflowComponentParamType.InputTable,
          title: 'Polygons table'
        },
        {
          name: 'geosource',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in table to clip',
          parent: 'source',

          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        },
        {
          name: 'geopolygons',
          type: WorkflowComponentParamType.Column,
          title: 'Geo column in polygons table',
          parent: 'polygons',
          dataType: [ColumnDataType.Geography, ColumnDataType.Geometry]
        }
      ]
    )
  }

  async getCode({
    workflow,
    params,
    connection,
    componentName,
    codeType
  }: WorkflowComponentCodeParams): Promise<QueryAndOutputs> {
    const outputTablename = connection.getOutputTableName(workflow.id, componentName, params, 'result', codeType)
    const preparedParams = this._prepareParameters(params)

    const leftOutputTableName = connection.quoteName(outputTablename + '_left')
    const rightOutputTableName = connection.quoteName(outputTablename + '_right')
    const select = dedent`WITH source AS (
      SELECT /*+ REPARTITION(40, ${preparedParams.geosource}) */
      *, ${connection.processInputGeogColumn(preparedParams.geosource as string)} AS ${
      preparedParams.geosource
    }_processed FROM ${leftOutputTableName}
    ),
    polygons AS (
      SELECT /*+ REPARTITION(40, ${preparedParams.geopolygons}) */
      *, ${connection.processInputGeogColumn(preparedParams.geopolygons as string)} AS ${
      preparedParams.geopolygons
    }_processed FROM ${rightOutputTableName}
    )
    SELECT /*+ REPARTITION(40, ${params.geosource}_clipped) */
              ${connection.processOutputGeogColumn(`ST_INTERSECTION(polyg.geo, s.${params.geosource}_processed)`)}
                AS ${params.geosource}_clipped, s.* EXCEPT(${
      preparedParams.geosource
    }_processed, __carto_bounds_xmin, __carto_bounds_xmax, __carto_bounds_ymin, __carto_bounds_ymax)
    FROM
      source s,
      (
      SELECT
        MIN(__carto_bounds_xmin) AS __carto_bounds_xmin,
        MAX(__carto_bounds_xmax) AS __carto_bounds_xmax,
        MIN(__carto_bounds_ymin) AS __carto_bounds_ymin,
        MAX(__carto_bounds_ymax) AS __carto_bounds_ymax,
        ST_UNION_AGG(${connection.processInputGeogColumn(params.geopolygons as string)}) geo
        FROM polygons
      ) polyg
    WHERE
        (s.__carto_bounds_xmax >= polyg.__carto_bounds_xmin
        AND s.__carto_bounds_xmin <= polyg.__carto_bounds_xmax
        AND s.__carto_bounds_ymax >= polyg.__carto_bounds_ymin
        AND s.__carto_bounds_ymin <= polyg.__carto_bounds_ymax)
      AND
        ST_INTERSECTS(
          polyg.geo,
          s.${preparedParams.geosource}_processed
        )`

    const createTable = (connection as DBricksConnection).wrapSQLCodeAsPythonCall(
      connection.getCreateTempTableQuery(outputTablename, select, codeType, null)
    )
    const generateSpatialFilterTablesQuery = (connection as DBricksConnection).generateSpatialFilterTables(
      connection.quoteName(preparedParams.source as string),
      connection.quoteName(preparedParams.polygons as string),
      preparedParams.geosource as string,
      preparedParams.geopolygons as string,
      leftOutputTableName,
      rightOutputTableName
    )
    const query = `${generateSpatialFilterTablesQuery}\n${createTable}`

    return {
      query,
      outputs: { result: outputTablename }
    }
  }

  language(provider: Provider): Language {
    return Language.Python
  }

  canProvideOutputSchemas(): boolean {
    return true
  }

  getOutputSchema({ columns }: GetOutputSchemaParams): TableSchema {
    return columns.source
  }
  supportedProviders(): Provider[] {
    return [Provider.DatabricksRest]
  }
}

export { ClipWithPolygonsDB }
